/**
 * Protected Route Components
 * Route wrappers for authentication and authorization
 */

import { Navigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth, useIsAuthenticatedUser } from './useAuth';
import type { UserRole } from '@/common/stores';

/**
 * ProtectedRoute wrapper
 * Redirects to login if not authenticated
 */
export interface ProtectedRouteProps {
  children: ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps): ReactNode {
  const isAuthenticated = useIsAuthenticatedUser();

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
}

/**
 * RoleBasedRoute wrapper
 * Restricts access based on user role
 */
export interface RoleBasedRouteProps {
  children: ReactNode;
  requiredRoles: UserRole | UserRole[];
  fallback?: ReactNode;
}

export function RoleBasedRoute({
  children,
  requiredRoles,
  fallback,
}: RoleBasedRouteProps): ReactNode {
  const user = useAuth().user;
  const isAuthenticated = useIsAuthenticatedUser();

  if (!isAuthenticated || !user) {
    return <Navigate to="/" replace />;
  }

  // Helper function to get all user roles
  const getAllUserRoles = (): string[] => {
    const roles: string[] = [];

    // Try roles array first (preferred - should have all roles)
    if (user.roles && Array.isArray(user.roles) && user.roles.length > 0) {
      roles.push(...user.roles);
    }

    // Try userRoles array
    if (user.userRoles && Array.isArray(user.userRoles) && user.userRoles.length > 0) {
      user.userRoles.forEach((ur) => {
        const roleName = ur.role?.name;
        if (roleName && !roles.includes(roleName)) {
          roles.push(roleName);
        }
      });
    }

    // Try primary role field (fallback)
    if (user.role && !roles.includes(user.role)) {
      roles.push(user.role);
    }

    return roles;
  };

  // Helper function to check if user has any of the required roles
  const userHasRequiredRole = (requiredRoles: UserRole | UserRole[]): boolean => {
    const userRoles = getAllUserRoles();
    const requiredRoleArray = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];

    // Check if any user role matches any required role
    return requiredRoleArray.some(requiredRole =>
      userRoles.some(userRole => userRole === requiredRole)
    );
  };

  const userRoles = getAllUserRoles();
  const hasAccess = userHasRequiredRole(requiredRoles);

  console.log('[RoleBasedRoute] Access check:', {
    userRoles,
    requiredRoles,
    hasAccess,
    user: {
      role: user.role,
      roles: user.roles,
      userRoles: user.userRoles
    }
  });

  if (!hasAccess) {
    console.warn('[RoleBasedRoute] Access denied - redirecting to /unauthorized');
    if (fallback) {
      return fallback;
    }
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
}

/**
 * AdminRoute wrapper
 * Restricts access to admins only
 */
export interface AdminRouteProps {
  children: ReactNode;
  fallback?: ReactNode;
}

export function AdminRoute({ children, fallback }: AdminRouteProps): ReactNode {
  return (
    <RoleBasedRoute requiredRoles="ADMIN" fallback={fallback}>
      {children}
    </RoleBasedRoute>
  );
}


/**
 * PublicRoute wrapper
 * Redirects authenticated users away from public pages (e.g., login, register)
 */
export interface PublicRouteProps {
  children: ReactNode;
  redirectTo?: string;
}

export function PublicRoute({ children, redirectTo = '/admin' }: PublicRouteProps): ReactNode {
  const isAuthenticated = useIsAuthenticatedUser();

  if (isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
}
